interface HumpsOptions {
  process?: HumpsProcessor;
  separator?: string;
  split?: RegExp;
}

interface HumpsProcessor {
  (key: string, convert: HumpsProcessorParameter, options?: HumpsOptions): string;
}

interface HumpsProcessorParameter {
  (key: string, options?: HumpsOptions): string;
}

type OptionOrProcessor = HumpsOptions | HumpsProcessor;

const _isFunction = (obj: unknown) => typeof obj === 'function';
const _isObject = (obj: unknown) => obj === Object(obj);
const _isArray = (obj: unknown) => toString.call(obj) === '[object Array]';
const _isDate = (obj: unknown) => toString.call(obj) === '[object Date]';
const _isRegExp = (obj: unknown) => toString.call(obj) === '[object RegExp]';
const _isBoolean = (obj: unknown) => toString.call(obj) === '[object Boolean]';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const _isNumerical = (obj: any) => {
  obj = obj - 0;
  return obj === obj;
};
const camelize = (string: string) => {
  if (_isNumerical(string)) return string;

  string = string.replace(/[-_\s]+(.)?/g, (_match, chr) => (chr ? chr.toUpperCase() : ''));
  // Ensure 1st char is always lowercase
  return string.substr(0, 1).toLowerCase() + string.substr(1);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const separateWords = (string: string, options: any) => {
  options = options || {};
  const separator = options.separator || '_';
  const split = options.split || /(?=[A-Z])/;

  return string.split(split).join(separator);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const decamelize = (string: string, options: any) => separateWords(string, options).toLowerCase();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const _processKeys = (convert: any, obj: any, options: any): any => {
  if (!_isObject(obj) || _isDate(obj) || _isRegExp(obj) || _isBoolean(obj) || _isFunction(obj))
    return obj;

  let output,
    i = 0,
    l = 0;

  if (_isArray(obj)) {
    output = [];
    for (l = obj.length; i < l; i++) output.push(_processKeys(convert, obj[i], options));
  } else {
    output = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (output as any)[convert(key, options) as any] = _processKeys(convert, obj[key], options);
      }
    }
  }
  return output;
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const _processor = (convert: any, options: any) => {
  const callback = options && 'process' in options ? options.process : options;

  if (typeof callback !== 'function') return convert;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (string: string, options: any) => callback(string, convert, options);
};

const camelizeKeys = (
  object: Record<string, unknown> | Record<string, unknown>[],
  options?: OptionOrProcessor,
): Record<string, unknown> | Record<string, unknown>[] =>
  _processKeys(_processor(camelize, options), object, options);

const decamelizeKeys = (
  object: Record<string, unknown> | Record<string, unknown>[],
  options?: OptionOrProcessor,
): Record<string, unknown> | Record<string, unknown>[] =>
  _processKeys(_processor(decamelize, options), object, options);

export { camelizeKeys, decamelizeKeys };
